/**
 * RENDER — visual identity constants.
 *
 * Single source for colors and feel-tuning, mirroring `docs/design-direction.md`.
 * Color = meaning: cyan = the line you follow, amber = go faster, red = danger,
 * violet = optional reward, green = moving/done. Keep these in sync with the doc.
 *
 * Colors are 0xRRGGBB ints (Three.js `Color` accepts them directly).
 */

import type { CourseElement } from '@/types';

export const PALETTE = {
  /** Near-black blue void; clear color + fog. Matches PWA theme_color #0b0e14. */
  void: 0x0b0e14,
  /** Drivable floor, matte. */
  track: 0x161b26,
  /** Cyan racing-line accent — the eye-magnet hue. */
  cyan: 0x2de2e6,
  /** Wall body. */
  wall: 0x1b2330,
  /** Emissive wall lip (cool blue) so boundaries read at speed. */
  wallLip: 0x3a7bd5,
  /** Ramp / shortcut accent (violet, "reward"). */
  ramp: 0x7c5cff,
  /** Booster (amber/gold, "go faster"). */
  booster: 0xffd23f,
  /** Hazard (hot magenta-red, "danger"). */
  hazard: 0xff3864,
  /** Mover (green, "alive/moving"). */
  mover: 0x19c37d,
  /** Passed checkpoint / success. */
  success: 0x19c37d,
  /** Finish gate — brightest object on screen. */
  finish: 0xffffff,
  /** Sky tint for the hemisphere/ambient term and env-map top. */
  sky: 0x1a2230,
  /** Ground tint for the hemisphere/ambient term and env-map bottom. */
  ground: 0x06080c,
} as const;

/** Feel/timing knobs. Tuned for "tight, never floaty" per the design doc. */
export const FEEL = {
  /** Chase camera position smoothing: fraction of gap remaining after 1s. Snappy. */
  camPosSmoothing: 0.0008,
  /** Look-at target smoothing — slightly looser so aim doesn't jitter. */
  camLookSmoothing: 0.004,
  /** Idle field of view (deg). */
  fovIdle: 60,
  /** FOV at max speed (deg) — widening sells velocity cheaply. */
  fovMax: 74,
  /** FOV smoothing (snappy but not instant, so speed changes feel weighty). */
  fovSmoothing: 0.02,
  /** Camera distance behind the marble at idle (world units). */
  camDistIdle: 7,
  /** Extra pull-back at max speed. */
  camDistSpeedGain: 3.5,
  /** Camera height above the marble. */
  camHeight: 3.2,
  /** Look-ahead distance along velocity at idle. */
  lookAheadIdle: 4,
  /** Extra look-ahead at max speed (reads the racing line earlier). */
  lookAheadSpeedGain: 9,
  /** Near-miss trigger: marble surface within this gap of a wall (world units). */
  nearMissGap: 0.55,
  /** Min speed (frac of maxSpeed) for a near-miss to count as "juice". */
  nearMissMinSpeedFrac: 0.45,
  /** Seconds a flash/kick takes to fully decay (critically, <200ms). */
  flashDecay: 0.18,
  /** Camera micro-kick magnitude on near-miss (world units). */
  nearMissKick: 0.18,
  /** FOV kick (deg) added on booster/shortcut. */
  boostFovKick: 6,
} as const;

/** Resolve the themed base color for a course element kind. */
export function elementColor(el: CourseElement): number {
  switch (el.kind) {
    case 'track':
      return PALETTE.track;
    case 'wall':
      return PALETTE.wall;
    case 'ramp':
      return PALETTE.ramp;
    case 'booster':
      return PALETTE.booster;
    case 'hazard':
      return PALETTE.hazard;
    case 'mover':
      return PALETTE.mover;
  }
}
